import * as React from "react";
import { useMemo } from "react";

import { ChartTooltip } from "@/components/Charts/ChartTooltip";
import { LegendDataIcon, type LegendDataIconProps } from "@/components/Charts/LegendDataIcon";
import { dataColors } from "@/components/Charts/theme";
import { FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { isDefined } from "@/helpers/util";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "@/ui-adapters/Recharts";
import { RuntimeHeight } from "@/ui-adapters/RuntimeHeight";

interface DataPoint<TCategory = string> {
  x: TCategory;
  y: number | undefined;
}

interface PortfolioOverviewChartProps {
  view: "rating" | "feelAtHome" | "adoption" | "engagement";
  month: Date;
  maxY?: number;
  dataPrimary: DataPoint[];
  dataBenchmark: DataPoint[];
  benchmarkLabel: string;
  formatYTick: (y: number) => string;
}

const BAR_HEIGHT_WITH_GAP = 45;
const AXIS_SPACE = 60;

export function PortfolioOverviewChart({
  dataPrimary,
  dataBenchmark,
  view,
  month,
  maxY,
  benchmarkLabel,
  formatYTick,
}: PortfolioOverviewChartProps): React.ReactNode {
  const chartData = useMemo(
    () =>
      dataPrimary.map((point) => {
        const primaryValue = dataPrimary.find((p) => p.x === point.x)?.y;
        const benchmarkValue = dataBenchmark.find((b) => b.x === point.x)?.y;

        return {
          x: point.x,
          primary: isDefined(primaryValue) && primaryValue >= 0 ? primaryValue : undefined,
          benchmark: isDefined(benchmarkValue) && benchmarkValue >= 0 ? benchmarkValue : undefined,
        };
      }),
    [dataPrimary, dataBenchmark],
  );

  const isSingleBenchmark = useMemo(
    () => dataBenchmark.filter((d) => isDefined(d.y) && d.y >= 0).length === 1,
    [dataBenchmark],
  );
  const singleBenchmarkValue = useMemo(
    () => (isSingleBenchmark ? dataBenchmark.find((d) => isDefined(d.y) && d.y >= 0)?.y : undefined),
    [isSingleBenchmark, dataBenchmark],
  );

  const barSize = 14;
  const calculatedHeight = chartData.length * BAR_HEIGHT_WITH_GAP + AXIS_SPACE;

  if (chartData.length === 0) {
    return null;
  }

  return (
    <RuntimeHeight height={calculatedHeight}>
      {(heightProps) => (
        <div className="w-full" {...heightProps}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart layout="vertical" data={chartData} barSize={barSize} margin={{ right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={dataColors.axis} />
              <XAxis
                type="number"
                domain={[0, maxY ?? "auto"]}
                tickCount={6}
                tickFormatter={formatYTick}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12 }}
                orientation="top"
              />
              <YAxis
                type="category"
                dataKey="x"
                tickFormatter={(value: string) => (value.length > 20 ? `${value.substring(0, 18)}…` : value)}
                tickLine={false}
                axisLine={{ stroke: dataColors.axis, strokeWidth: 0.5 }}
                tick={{ fontSize: 12 }}
                width={130}
                interval={0}
              />
              <RechartsTooltip
                content={
                  <CustomTooltip
                    isSingleBenchmark={isSingleBenchmark}
                    singleBenchmarkValue={singleBenchmarkValue}
                    view={view}
                    month={month}
                    benchmarkLabel={benchmarkLabel}
                  />
                }
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />
              <Bar
                dataKey="primary"
                fill={dataColors.primary}
                // Recharts will skip rendering if value is 0
                minPointSize={Number.MIN_VALUE}
                radius={[0, barSize / 2, barSize / 2, 0]}
              />
              {isSingleBenchmark && isDefined(singleBenchmarkValue) ? (
                <ReferenceLine x={singleBenchmarkValue} stroke={dataColors.benchmark} strokeWidth={2}>
                  <Label value={benchmarkLabel} position="top" fill={dataColors.benchmark} fontSize={12} dy={-15} />
                </ReferenceLine>
              ) : (
                <Line
                  type="linear"
                  dataKey="benchmark"
                  stroke={dataColors.benchmark}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  connectNulls={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </RuntimeHeight>
  );
}

function CustomTooltip({
  active,
  payload,
  isSingleBenchmark,
  singleBenchmarkValue,
  view,
  month,
  benchmarkLabel,
}: Partial<{
  active?: boolean;
  payload?: {
    payload: {
      x: string;
      primary?: number;
      benchmark?: number;
    };
  }[];
}> & {
  isSingleBenchmark: boolean;
  singleBenchmarkValue?: number;
  view: "rating" | "feelAtHome" | "adoption" | "engagement";
  month: Date;
  benchmarkLabel: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const data = payload[0].payload;
  const primaryValue = data.primary;
  const benchmarkValue = isSingleBenchmark ? singleBenchmarkValue : data.benchmark;

  return (
    <ChartTooltip>
      <table>
        <tbody>
          <tr>
            <td colSpan={4} className="pb-space-xs">
              <FormattedDate date={month} format="monthYear" />
            </td>
          </tr>
          {(view === "rating" || view === "feelAtHome") && (
            <>
              <StarRow title={data.x} value={primaryValue} icon={{ fill: dataColors.primary, type: "square" }} />
              {benchmarkValue && (
                <StarRow
                  title={benchmarkLabel}
                  value={benchmarkValue}
                  icon={{ fill: dataColors.benchmark, type: "minus" }}
                />
              )}
            </>
          )}
          {(view === "adoption" || view === "engagement") && (
            <>
              <PercentageRow title={data.x} value={primaryValue} icon={{ fill: dataColors.primary, type: "square" }} />
              {benchmarkValue && (
                <PercentageRow
                  title={benchmarkLabel}
                  value={benchmarkValue}
                  icon={{ fill: dataColors.benchmark, type: "minus" }}
                />
              )}
            </>
          )}
        </tbody>
      </table>
    </ChartTooltip>
  );
}

function StarRow({ title, icon, value }: { title: string; icon: LegendDataIconProps; value?: number }) {
  return (
    <tr>
      <td className="pr-space-sm">
        <LegendDataIcon {...icon} />
      </td>
      <td>{title}</td>
      <td className="pl-space-sm">
        <Star01Icon tone="rating" />
      </td>
      <td>
        <strong>{value?.toFixed(1) ?? "-"}</strong>
      </td>
    </tr>
  );
}

function PercentageRow({ title, icon, value }: { title: string; icon: LegendDataIconProps; value?: number }) {
  return (
    <tr>
      <td className="pr-space-sm">
        <LegendDataIcon {...icon} />
      </td>
      <td className="pr-space-sm">{title}</td>
      <td>
        <strong>{value ? value + "%" : "-"}</strong>
      </td>
    </tr>
  );
}
